import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  CircularProgress,
  TablePagination,
  Chip,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import ConfirmDialog from "../ConfirmDialog";
import * as XLSX from "xlsx";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function EventPartyDefaults() {
  const theme = useTheme();
  const [events, setEvents] = useState([]);
  const [query, setQuery] = useState("");
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [expandedRows, setExpandedRows] = useState({});

  //---------------------------Confirm dialog states----------------------------------//
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(false);

  //---------------------------Add Event Party Modal states----------------------------------//
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newEventParty, setNewEventParty] = useState({
    controller: "",
    sd: "",
    alarm: false,
    description: "",
    party: "not assigned",
  });
  //-------------------------- Import Event PArty hidden input -----------------------------//
  const [controllerFilter, setControllerFilter] = useState("");

  const fileInputRef = React.useRef(null);
  const REQUIRED_SHEETS = ["C5", "WP", "PLG1", "PLG2", "PLG3", "PLG4"];
  const REQUIRED_HEADERS = [
    "SD",
    "Controller",
    "Alarm",
    "Description",
    "Party",
  ];

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const confirmDelete = (event) => {
    setSelectedEvent(event);
    setConfirmOpen(true);
  };

  //---------------------------Add Event Party Modal functions----------------------------------//
  const openAddModal = () => {
    setNewEventParty({
      controller: "",
      sd: "",
      description: "",
      party: "not assigned",
    });
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setNewEventParty({
      controller: "",
      sd: "",
      description: "",
      party: "not assigned",
    });
  };

  const handleInputChange = (field, value) => {
    setNewEventParty((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!newEventParty.controller.trim()) {
      toast.error("Controller is required");
      return false;
    }
    if (!newEventParty.sd || newEventParty.sd.toString().trim() === "") {
      toast.error("SD Code is required");
      return false;
    }
    if (parseInt(newEventParty.sd) === 0) {
      toast.error("SD Code cannot be 0");
      return false;
    }
    if (!newEventParty.description.trim()) {
      toast.error("Event Description is required");
      return false;
    }

    return true;
  };

  const handleAddEventParty = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      const payload = {
        controller: newEventParty.controller.trim(),
        sd: parseInt(newEventParty.sd),
        alarm: newEventParty.alarm,
        description: newEventParty.description.trim(),
        party:
          newEventParty.party === "not assigned" ? null : newEventParty.party,
        companyId: process.env.REACT_APP_COMPANY_ID,
        UI_Description: `${getUIDescription()}\n Add Event Party`,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/eventParties`,
        payload
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("✅ Event Party added successfully!");

        const newEvent = {
          id: response.data.id || Date.now(),
          ...payload,
          party: payload.party || "not assigned",
        };

        setEvents((prev) => [...prev, newEvent]);
        setFilteredEvents((prev) => [...prev, newEvent]);

        closeAddModal();
      }
    } catch (error) {
      console.error("Error adding event party:", error);
      toast.error(
        error.response?.data?.message || "❌ Failed to add event party"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  //---------------------------Existing functions----------------------------------//

  const sortEvents = (eventsToSort) => {
    if (!sortConfig.key) return eventsToSort;

    return [...eventsToSort].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === "boolean") {
        const aNum = aValue ? 1 : 0;
        const bNum = bValue ? 1 : 0;
        return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      if (typeof aValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue?.localeCompare(bValue)
          : bValue?.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return 0;
    });
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const fetchEventPartyData = async () => {
    try {
      setLoadingData(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/eventParties`,
        {
          params: { companyId: process.env.REACT_APP_COMPANY_ID },
        }
      );
      const transformedData = response.data;
      setEvents(transformedData);
      setFilteredEvents(transformedData);
    } catch (err) {
      console.error("Error fetching event party defaults:", err);
      toast.error("❌ Failed to fetch event party data");
    } finally {
      setLoadingData(false);
    }
  };

  const handleDeleteEventParty = async (eventParty) => {
    try {
      let eventId = eventParty.id;
      if (!eventId) {
        console.error("❌ Missing eventId for deletion");
        return;
      }

      await axios.delete(
        `${process.env.REACT_APP_API_GATEAWAY}/eventParties/${eventParty.id}/hard`, {
        data: { UI_Description: `${getUIDescription()}\n Delete Event Party`, }
      }
      );

      console.log("✅ eventParty deleted:", eventParty);

      setEvents((prevEvents) => prevEvents.filter((r) => !(r.id === eventId)));
      setFilteredEvents((prevFiltered) =>
        prevFiltered.filter((r) => !(r.id === eventId))
      );

      toast.success("✅ Event Party deleted successfully!");
    } catch (error) {
      console.error(
        "❌ Error deleting eventParty:",
        error.response?.data || error.message
      );
      toast.error("❌ Failed to delete event party");
    } finally {
      setConfirmOpen(false);
      setSelectedEvent(null);
    }
  };

  useEffect(() => {
    fetchEventPartyData();
  }, []);

  const handleSearch = (e) => {
  const q = e.target.value.toLowerCase();
  setQuery(q);
  applyFilters(q, controllerFilter);
  setPage(0);
};

const applyFilters = (sdQuery, controller) => {
  const filtered = events.filter((event) => {
    const sd = event.sd?.toString() || "";
    const matchesSd = sdQuery === "" || sd === sdQuery;
    const matchesController = controller === "" || event.controller === controller;
    return matchesSd && matchesController;
  });
  setFilteredEvents(filtered);
};

const handleControllerFilter = (value) => {
  setControllerFilter(value);
  applyFilters(query, value);
  setPage(0);
};
  

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePartyChange = async (event, newParty) => {
    try {
      console.log(
        `Updating SD Code ${event.sd} (${event.controller}) party to ${newParty}`
      );

      setEvents((prevEvents) =>
        prevEvents.map((e) =>
          e.id === event.id ? { ...e, party: newParty } : e
        )
      );
      setFilteredEvents((prevFiltered) =>
        prevFiltered.map((e) =>
          e.id === event.id ? { ...e, party: newParty } : e
        )
      );
    } catch (error) {
      console.error("Error updating party:", error);
    }
  };

  const handleTypeAlarmChange = async (event, newAlarmValue) => {
    try {
      // Convert string to boolean
      const alarmBoolean = newAlarmValue === "true";

      console.log(
        `Updating SD Code ${event.sd} (${event.controller}) alarm to ${alarmBoolean}`
      );

      // Update local state only (just like handlePartyChange does)
      setEvents((prevEvents) =>
        prevEvents.map((e) =>
          e.id === event.id ? { ...e, alarm: alarmBoolean } : e
        )
      );
      setFilteredEvents((prevFiltered) =>
        prevFiltered.map((e) =>
          e.id === event.id ? { ...e, alarm: alarmBoolean } : e
        )
      );
    } catch (error) {
      console.error("Error updating type:", error);
    }
  };

  const editEvent = async (event) => {
    try {
      const payload = {
        controller: event.controller,
        sd: event.sd,
        alarm: event.alarm, // ADD THIS LINE
        description: event.description,
        party: event.party == "not assigned" ? null : event.party,
        UI_Description: `${getUIDescription()}\n Update Event Party`,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/eventParties/${event.id}`,
        payload
      );
      if (response.status == 200) {
        toast.success("✅ notification group updated successfully!");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "❌ Failed to update notification group"
      );
    }
  };

  const sortedEvents = sortEvents(filteredEvents);
  const paginatedEvents = sortedEvents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  //--------------------------- Import / Export functions ----------------------------------//
  const exportEventParty = () => {
    try {
      if (!events || events.length === 0) {
        toast.warning("No event data to export");
        return;
      }

      const workbook = XLSX.utils.book_new();

      REQUIRED_SHEETS.forEach((sheetName) => {
        // Filter events by controller
        const sheetEvents = events
          .filter((e) => String(e.controller).trim() === sheetName)
          .sort((a, b) => {
            if (a.sd == null) return 1;
            if (b.sd == null) return -1;
            return String(a.sd).localeCompare(String(b.sd), undefined, {
              numeric: true,
              sensitivity: "base",
            });
          })
          .map((event) => ({
            SD: event.sd,
            Controller: sheetName,
            Alarm: event.alarm ? "AL" : "SD",
            Description: event.description ?? "",
            Party: event.party ?? "not assigned",
          }));

        // Ensure header-only sheet if no data
        const worksheet = XLSX.utils.json_to_sheet(sheetEvents, {
          header: REQUIRED_HEADERS,
          skipHeader: false,
        });

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });

      XLSX.writeFile(workbook, "event_parties.xlsx");
      toast.success("Event Party exported successfully");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export Event Party");
    }
  };

  const handleImportEventParty = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoadingData(true);
      const reader = new FileReader();

      reader.onload = async (evt) => {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // SHEET ORDER VALIDATION
        const actualOrder = workbook.SheetNames;
        const indexMap = Object.fromEntries(
          actualOrder.map((name, index) => [name, index])
        );

        // VALIDATE SHEETS NAMES
        if (workbook.SheetNames.length !== REQUIRED_SHEETS.length) {
          toast.error(
            `Excel file must contain exactly ${REQUIRED_SHEETS.length
            } sheets: ${REQUIRED_SHEETS.join(", ")}`,
            { autoClose: 6000 }
          );
          setLoadingData(false);
          return;
        }
        const missingSheets = REQUIRED_SHEETS.filter(
          (s) => !workbook.SheetNames.includes(s)
        );
        if (missingSheets.length > 0) {
          toast.error(`Missing required sheets: ${missingSheets.join(", ")}`, {
            autoClose: 6000,
          });
          setLoadingData(false);
          return;
        }

        // Validate relative order
        for (let i = 0; i < REQUIRED_SHEETS.length - 1; i++) {
          const current = REQUIRED_SHEETS[i];
          const next = REQUIRED_SHEETS[i + 1];

          if (indexMap[current] > indexMap[next]) {
            toast.error(
              `Invalid sheet order. "${current}" must come before "${next}".`,
              { autoClose: 8000 }
            );
            setLoadingData(false);
            return;
          }
        }

        const allRows = [];
        // GLOBAL DESCRIPTION DUPLICATE CHECK (across all sheets)
        const descriptionMap = new Map();
        for (const sheetName of REQUIRED_SHEETS) {
          const worksheet = workbook.Sheets[sheetName];

          // HEADER + EXTRA COLUMN VALIDATION
          const headerRow = (
            XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              range: 0,
            })[0] || []
          ).map((h) => (h ? String(h).trim() : ""));

          const headerIndexMap = Object.fromEntries(
            headerRow.map((h, index) => [h, index])
          );

          // Missing required headers
          const missingHeaders = REQUIRED_HEADERS.filter(
            (h) => !headerRow.includes(h)
          );

          if (missingHeaders.length > 0) {
            toast.error(
              `Sheet "${sheetName}" is missing headers: ${missingHeaders.join(
                ", "
              )}`,
              { autoClose: 8000 }
            );
            setLoadingData(false);
            return;
          }

          // Extra header names
          const extraHeaders = headerRow.filter(
            (h) => h && !REQUIRED_HEADERS.includes(h)
          );

          if (extraHeaders.length > 0) {
            toast.error(
              `Sheet "${sheetName}" contains invalid header columns: ${extraHeaders.join(
                ", "
              )}`,
              { autoClose: 8000 }
            );
            setLoadingData(false);
            return;
          }

          // Validate relative order of header
          for (let i = 0; i < REQUIRED_HEADERS.length - 1; i++) {
            const current = REQUIRED_HEADERS[i];
            const next = REQUIRED_HEADERS[i + 1];

            if (headerIndexMap[current] > headerIndexMap[next]) {
              toast.error(
                `Invalid header order in sheet "${sheetName}". "${current}" must come before "${next}".`,
                { autoClose: 8000 }
              );
              setLoadingData(false);
              return;
            }
          }

          // ANY DATA OUTSIDE ALLOWED COLUMNS (A–E)
          const range = XLSX.utils.decode_range(worksheet["!ref"]);
          const allowedColumnCount = REQUIRED_HEADERS.length; // 5

          for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = allowedColumnCount; C <= range.e.c; ++C) {
              const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
              const cell = worksheet[cellAddress];

              if (cell && cell.v !== undefined && cell.v !== "") {
                toast.error(
                  `Invalid data found outside allowed columns in sheet "${sheetName}", cell ${cellAddress}`,
                  { autoClose: 8000 }
                );
                setLoadingData(false);
                return;
              }
            }
          }

          // Read data rows
          const rows = XLSX.utils.sheet_to_json(worksheet, {
            defval: null,
            blankrows: true,
          });

          if (rows.length === 0) {
            toast.error(`Sheet "${sheetName}" is empty`, { autoClose: 6000 });
            setLoadingData(false);
            return;
          }

          const sdMap = new Map();

          for (let index = 0; index < rows.length; index++) {
            const row = rows[index];
            const excelRow = index + 2;
            // Fully empty row
            const isEmptyRow = REQUIRED_HEADERS.every(
              (h) => row[h] === null || row[h] === "" || row[h] === undefined
            );

            if (isEmptyRow) {
              toast.error(
                `Empty row found in sheet "${sheetName}", row ${excelRow}. Empty rows are not allowed.`,
                { autoClose: 8000 }
              );
              setLoadingData(false);
              return;
            }

            // REQUIRED ROW VALIDATION
            for (const field of REQUIRED_HEADERS) {
              if (
                row[field] === null ||
                row[field] === "" ||
                row[field] === undefined
              ) {
                toast.error(
                  `Empty "${field}" in sheet "${sheetName}", row ${excelRow}`,
                  { autoClose: 8000 }
                );
                setLoadingData(false);
                return;
              }
            }

            // CONTROLLER MISMATCH
            if (String(row.Controller).trim() !== sheetName) {
              toast.error(
                `Controller mismatch in sheet "${sheetName}", row ${excelRow}. Found "${row.Controller}"`,
                { autoClose: 8000 }
              );
              setLoadingData(false);
              return;
            }

            // SD MUST BE A NUMBER
            const sdValue = row.SD;
            if (
              sdValue === null ||
              sdValue === "" ||
              Number.isNaN(Number(sdValue)) || sdValue <= 0
            ) {
              toast.error(
                `Invalid SD value in sheet "${sheetName}", row ${excelRow}. SD must be a number.`,
                { autoClose: 6000 }
              );
              setLoadingData(false);
              return;
            }

            // CHECK FOR DUPLICATE SD
            const sdNumber = Number(sdValue);
            if (sdMap.has(sdNumber)) {
              toast.error(
                `Duplicate SD code "${sdNumber}" found in sheet "${sheetName}", rows ${sdMap.get(
                  sdNumber
                )} and ${excelRow}`,
                { autoClose: 8000 }
              );
              setLoadingData(false);
              return;
            }
            sdMap.set(sdNumber, excelRow);

            // DESCRIPTION MUST NOT BE PURELY NUMERIC
            const descriptionValue = String(row.Description).trim();

            // Regex: only digits (optionally with spaces)
            const isNumericOnly = /^[0-9\s]+$/.test(descriptionValue);

            if (isNumericOnly) {
              toast.error(
                `Description cannot be numeric only in sheet "${sheetName}", row ${excelRow}.`,
                { autoClose: 7000 }
              );
              setLoadingData(false);
              return;
            }

            // DUPLICATE DESCRIPTION CHECK (GLOBAL)
            if (sheetName !== "WP") {
              const rawDescription = String(row.Description).trim();
              const normalizedDescription = rawDescription.toLowerCase(); // normalize

              if (descriptionMap.has(normalizedDescription)) {
                const prev = descriptionMap.get(normalizedDescription);

                toast.error(
                  `Duplicate Description found.\n` +
                  `"${rawDescription}" appears in:\n` +
                  `• Sheet "${prev.sheetName}", row ${prev.row}\n` +
                  `• Sheet "${sheetName}", row ${excelRow}`,
                  { autoClose: 9000 }
                );
                setLoadingData(false);
                return;
              }

              descriptionMap.set(normalizedDescription, {
                sheetName,
                row: excelRow,
              });
            }



            // PARTY ENUM
            const allowedParties = ["company", "customer"];
            const partyValue = String(row.Party).trim().toLowerCase();
            if (!allowedParties.includes(partyValue)) {
              toast.error(
                `Invalid Party value in sheet "${sheetName}", row ${excelRow}. Allowed values: Company or Customer`,
                { autoClose: 6000 }
              );
              setLoadingData(false);
              return;
            }

            // ALARM ENUM
            const allowedAlarms = ["AL", "SD"];
            if (!allowedAlarms.includes(String(row.Alarm).trim())) {
              toast.error(
                `Invalid Alarm value in sheet "${sheetName}", row ${excelRow}. Allowed values: AL or SD`,
                { autoClose: 6000 }
              );
              setLoadingData(false);
              return;
            }

            allRows.push({
              sd: Number(row.SD),
              controller: sheetName,
              alarm: row.Alarm === "AL",
              description: row.Description.trim(),
              party: partyValue,
              __rowIndex: excelRow,
            });
          };
        }

        console.log("VALIDATED ROWS:", allRows);
        await importEventParties(allRows);

        setLoadingData(false);
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Excel parsing failed:", error);
      toast.error("Failed to read Excel file");
      setLoadingData(false);
    } finally {
      e.target.value = null;
    }
  };

  const importEventParties = async (rows) => {
    try {
      setIsSubmitting(true);
      const items = rows.map((row, index) => {
        if (!row.sd || !row.controller) {
          throw new Error(`Invalid row at line ${index + 2}`);
        }
        return {
          controller: String(row.controller).trim(),
          sd: parseInt(row.sd),
          alarm: row.alarm,
          description: row.description?.trim() || "",
          party: !row.party || row.party === "not assigned" ? null : row.party,
          companyId: process.env.REACT_APP_COMPANY_ID,
        };
      });
      const payload = {
        items,
        UI_Description: `${getUIDescription()}\n Add multiple Event Parties`,
      };
      // Send to backend (bulk insert)
      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/eventParties/bulk`,
        payload
      );
      if (response.status === 200 || response.status === 201) {
        const { created, updated, revived, message } = response.data;

        // Check if nothing changed
        if (created === 0 && updated === 0 && revived === 0) {
          toast.info(message || "Nothing changed");
        } else {
          // Show success message with counts
          const messages = [];
          if (created > 0) messages.push(`${created} created`);
          if (updated > 0) messages.push(`${updated} updated`);
          if (revived > 0) messages.push(`${revived} revived`);

          toast.success(messages.join(", "));
        }

        // Always reload from backend to get IDs
        await fetchEventPartyData();
      }
    } catch (error) {
      console.error("Import failed:", error);
      toast.error(
        error.message ||
        error.response?.data?.message ||
        "Failed to import event parties"
      );
      setLoadingData(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 0, sm: 0 },
        height: "100%",
        position: "relative",
      }}
    >
      {loadingData && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(187, 183, 183, 0.12)",
            backdropFilter: "blur(10px)",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="#60a5fa" size={70} />
        </Box>
      )}

      <Paper
        elevation={4}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              fontSize={{ xs: "1.15rem", sm: "2rem", md: "2rem" }}
              fontWeight="bold"
            >
              Event Party Defaults
            </Typography>
            <Chip
              label={`${filteredEvents.length} events`}
              color="primary"
              size="small"
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "column",
              lg: "row",
            },
            alignItems: "center",
            gap: 2,
            mb: 2,
            mt: 2,
          }}
        >
          <TextField
            label="Search by SD Code"
            placeholder="Search by SD Code"
            variant="outlined"
            fullWidth
            value={query}
            onChange={handleSearch}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, // ✅ remove rounding
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              width: { xs: "100%", sm: "100%", md: "100%", lg: "auto" },
            }}
          >


          <FormControl
  sx={{
    flex: { xs: "1 1 100%", sm: "1 1 auto", lg: "0 0 auto" },
    minWidth: { sm: 140 },
    height: { xs: 45, sm: 45, md: 50 },
    borderRadius: 0,
    "& .MuiInputBase-root": {
      height: { xs: 45, sm: 45, md: 50 },
      borderRadius: 0,
    },
  }}
>
  <InputLabel>Controller</InputLabel>
  <Select
    value={controllerFilter}
    onChange={(e) => handleControllerFilter(e.target.value)}
    label="Controller"
    sx={{ borderRadius: 0 }}
  >
    <MenuItem value="">All</MenuItem>
    <MenuItem value="C5">C5</MenuItem>
    <MenuItem value="WP">WP</MenuItem>
    <MenuItem value="PLG1">PLG1</MenuItem>
    <MenuItem value="PLG2">PLG2</MenuItem>
    <MenuItem value="PLG3">PLG3</MenuItem>
    <MenuItem value="PLG4">PLG4</MenuItem>
  </Select>
</FormControl>


            <Button
              variant="contained"
              color="primary"
              sx={{
                borderRadius: 0,
                // flex: { xs: 0.5, sm: 0.25 }, // ⬅️ more space
                height: { xs: 45, sm: 45, md: 50 },
                fontSize: { xs: 10, sm: 12, md: 14 },
              }}
              onClick={openAddModal}
            >
              Add Event Party
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{
                borderRadius: 0,
                // flex: { xs: 0.5, sm: 0.25 }, // ⬅️ more space
                height: { xs: 45, sm: 45, md: 50 },
                fontSize: { xs: 10, sm: 12, md: 14 },
              }}
              onClick={() => fileInputRef.current.click()}
            >
              Import Event Party
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{
                borderRadius: 0,
                // flex: { xs: 0.5, sm: 0.25 }, // ⬅️ more space
                height: { xs: 45, sm: 45, md: 50 },
                fontSize: { xs: 10, sm: 12, md: 14 },
              }}
              disabled={isSubmitting}
              onClick={exportEventParty}
            >
              Export Event Party
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls,.csv"
              hidden
              onChange={handleImportEventParty}
            />
          </Box>
        </Box>

        <TableContainer
          sx={{
            flexGrow: 1,
            height: { xs: "50vh", md: "57.7vh" },
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "none",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                {/* Mobile expand button column */}
                <TableCell
                  sx={{
                    display: { xs: "table-cell", md: "none" },
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "12px 8px",
                    width: "50px",
                  }}
                >
                  Details
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                  onClick={() => handleSort("sd")}
                >
                  SD Code
                  {sortConfig.key === "sd" && (
                    <span>{sortConfig.direction === "asc" ? " ▲" : " ▼"}</span>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                  onClick={() => handleSort("controller")}
                >
                  Controller
                  {sortConfig.key === "controller" && (
                    <span>{sortConfig.direction === "asc" ? " ▲" : " ▼"}</span>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                  onClick={() => handleSort("alarm")}
                >
                  Alarm
                  {sortConfig.key === "alarm" && (
                    <span>{sortConfig.alarm === "asc" ? " ▲" : " ▼"}</span>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                  onClick={() => handleSort("description")}
                >
                  Event Description
                  {sortConfig.key === "description" && (
                    <span>{sortConfig.direction === "asc" ? " ▲" : " ▼"}</span>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                // onClick={() => handleSort("description")}
                >
                  Type
                  {/* {sortConfig.key === "description" && (
                    <span>{sortConfig.direction === "asc" ? " ▲" : " ▼"}</span>
                  )} */}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                  onClick={() => handleSort("party")}
                >
                  Party
                  {sortConfig.key === "party" && (
                    <span>{sortConfig.direction === "asc" ? " ▲" : " ▼"}</span>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    textAlign: "center",
                    padding: "8px",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEvents.length > 0 ? (
                paginatedEvents.map((event, index) => (
                  <React.Fragment key={event.id}>
                    <TableRow
                      hover
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        "&:hover": {
                          backgroundColor: "#4a4d54",
                        },
                      }}
                    >
                      {/* Expand Button - Mobile Only */}
                      <TableCell
                        sx={{
                          display: { xs: "table-cell", md: "none" },
                          textAlign: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(event.id)}
                          sx={{ color: "white" }}
                        >
                          {expandedRows[event.id] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {event.sd}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        <Chip
                          label={event.controller}
                          size="small"
                          variant="outlined"
                          color={
                            event.controller === "C5" ? "primary" : "white"
                          }
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        <Chip
                          label={event.alarm ? "AL" : "SD"}
                          size="small"
                          variant="outlined"
                          sx={{
                            color: event.alarm ? "#b49428ff" : "#f44336",
                            borderColor: event.alarm ? "#b49428ff" : "#f44336",
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        <Typography variant="body2">
                          {event.description ?? "No event"}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        <Select
                          value={event.alarm ? "true" : "false"}
                          onChange={(e) =>
                            handleTypeAlarmChange(event, e.target.value)
                          }
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="true">Alarm</MenuItem>
                          <MenuItem value="false">SD</MenuItem>
                        </Select>
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        <Select
                          value={event.party ?? "not assigned"}
                          onChange={(e) =>
                            handlePartyChange(event, e.target.value)
                          }
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="company">Company</MenuItem>
                          <MenuItem value="customer">Customer</MenuItem>
                          <MenuItem value="not assigned">Not Assigned</MenuItem>
                        </Select>
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => editEvent(event)}
                            sx={{
                              borderRadius: 0, // ✅ remove rounding
                            }}
                          >
                            Update
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => confirmDelete(event)}
                            sx={{
                              borderRadius: 0, // ✅ remove rounding
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Mobile Collapsed Row */}
                    <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={4}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        }}
                      >
                        <Collapse
                          in={expandedRows[event.id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Alarm:
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip
                                    label={event.alarm ? "AL" : "N/A"}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      color: event.alarm
                                        ? "#f44336"
                                        : "#666961ff",
                                      borderColor: event.alarm
                                        ? "#f44336"
                                        : "#666961ff",
                                    }}
                                  />
                                </Box>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Event Description:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {event.description ?? "No event"}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Type:
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 0.5,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 0, // ✅ remove rounding
                                    },
                                  }}
                                >
                                  <Select
                                    value={event.alarm ? "true" : "false"}
                                    onChange={(e) =>
                                      handleTypeAlarmChange(
                                        event,
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    fullWidth
                                  >
                                    <MenuItem value="true">Alarm</MenuItem>
                                    <MenuItem value="false">SD</MenuItem>
                                  </Select>
                                </Box>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Party:
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 0.5,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 0, // ✅ remove rounding
                                    },
                                  }}
                                >
                                  <Select
                                    value={event.party ?? "not assigned"}
                                    onChange={(e) =>
                                      handlePartyChange(event, e.target.value)
                                    }
                                    size="small"
                                    fullWidth
                                  >
                                    <MenuItem value="company">Company</MenuItem>
                                    <MenuItem value="customer">
                                      Customer
                                    </MenuItem>
                                    <MenuItem value="not assigned">
                                      Not Assigned
                                    </MenuItem>
                                  </Select>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No event data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredEvents.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          sx={{ mt: 2 }}
        />

        <Dialog
          open={addModalOpen}
          onClose={closeAddModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight="bold">
              Add New Event Party
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            >
              <FormControl
                sx={{
                  flex: { xs: "1 1 100%", sm: "1 1 auto", lg: "0 0 auto" },
                  minWidth: { sm: 140 },
                  height: { xs: 45, sm: 45, md: 50 },
                  borderRadius: 0,
                  "& .MuiInputBase-root": {
                    height: { xs: 45, sm: 45, md: 50 },
                    borderRadius: 0,
                  },
                }}
              >
                <InputLabel>Controller</InputLabel>
                <Select
                  value={newEventParty.controller}
                  onChange={(e) => handleInputChange("controller", e.target.value)}
                  label="Controller"
                  sx={{ borderRadius: 0 }}
                >
                  <MenuItem value="C5">C5</MenuItem>
                  <MenuItem value="WP">WP</MenuItem>
                  <MenuItem value="PLG1">PLG1</MenuItem>
                  <MenuItem value="PLG2">PLG2</MenuItem>
                  <MenuItem value="PLG3">PLG3</MenuItem>
                  <MenuItem value="PLG4">PLG4</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="SD Code"
                type="number"
                value={newEventParty.sd}
                onChange={(e) => handleInputChange("sd", e.target.value)}
                fullWidth
                required
              />

              <FormControl fullWidth>
                <InputLabel>Alarm</InputLabel>
                <Select
                  value={newEventParty.alarm}
                  onChange={(e) => handleInputChange("alarm", e.target.value)}
                  label="Alarm"
                >
                  <MenuItem value={true}>True</MenuItem>
                  <MenuItem value={false}>False</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Event Description"
                value={newEventParty.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                fullWidth
                multiline
                rows={3}
                required
              />

              <FormControl fullWidth>
                <InputLabel>Party</InputLabel>
                <Select
                  value={newEventParty.party}
                  onChange={(e) => handleInputChange("party", e.target.value)}
                  label="Party"
                >
                  <MenuItem value="company">Company</MenuItem>
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="not assigned">Not Assigned</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{
                borderRadius: 0, // ✅ remove rounding
              }}
              onClick={closeAddModal}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddEventParty}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{
                borderRadius: 0, // ✅ remove rounding
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress color="#60a5fa" size={16} />
                  Adding...
                </Box>
              ) : (
                "Add Event Party"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => handleDeleteEventParty(selectedEvent)}
        />
      </Paper>
    </Box>
  );
}
